"use client"
import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Globe, HelpCircle, Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import Link from "next/link";

interface NavigationLink {
  name: string;
  href: string;
  className?: string;
}

interface NavigationGroup {
  group: string;
  links: NavigationLink[];
}

interface NavigationItem {
  name: string;
  href: string;
  icon?: JSX.Element;
  items?: NavigationLink[] | NavigationGroup[];
}

const navigation: NavigationItem[] = [
  {
    name: "Trading",
    href: "#trading",
    items: [{
      group: "Accounts",
      links: [{
        name: "Standard accounts",
        href: "/standard-accounts"
      }, {
        name: "Professional accounts",
        href: "/pro-accounts"
      }, {
        name: "Demo trading account",
        href: "/demo-account"
      }]
    }, {
      group: "Conditions",
      links: [{
        name: "Deposits",
        href: "/deposits"
      }, {
        name: "Withdrawals",
        href: "/withdrawals"
      }, {
        name: "Client protection",
        href: "/client-protection"
      }, {
        name: "100% A-Book Order Execution",
        href: "/order-execution"
      }]
    }]
  },
  {
    name: "Markets",
    href: "#markets",
    items: [{
      group: "Markets",
      links: [{
        name: "Forex CFD",
        href: "/forex"
      }, {
        name: "Metals and Energies CFD",
        href: "/metals-and-energies"
      }, {
        name: "Stocks CFD",
        href: "/stocks"
      }, {
        name: "Indices CFD",
        href: "/indices"
      }, {
        name: "Crypto CFD",
        href: "/crypto"
      }]
    }]
  },
  {
    name: "Platforms",
    href: "#platforms",
    items: [{
      group: "Desktop",
      links: [{
        name: "cTrader Desktop",
        href: "/ctrader"
      }]
    }, {
      group: "Mobile",
      links: [{
        name: "cTrader iPhone/iPad",
        href: "/ctrader-iphone"
      }, {
        name: "cTrader Android",
        href: "/ctrader-android"
      }]
    }, {
      group: "Web",
      links: [{
        name: "cTrader Web",
        href: "/ctrader-web"
      }]
    }, {
      group: "Mac",
      links: [{
        name: "cTrader iMac",
        href: "/ctrader-imac"
      }]
    }, {
      group: "Trading Features",
      links: [{
        name: "cTrader Automate",
        href: "/ctrader-automate"
      }, {
        name: "cTrader Copy",
        href: "/ctrader-copy"
      }]
    }]
  },
  {
    name: "Tools",
    href: "#tools",
    items: [{
      group: "Tools",
      links: [{
        name: "Loss recovery calculator",
        href: "/loss-recovery-calculator"
      }]
    }]
  },
  {
    name: "Company",
    href: "#company",
    items: [{
      group: "About",
      links: [{
        name: "About us",
        href: "/about-us"
      }, {
        name: "Why Choose FXnity",
        href: "/why-choose-fxnity"
      }, {
        name: "How does FXnity make money",
        href: "/how-fxnity-makes-money"
      }, {
        name: "Blog",
        href: "/blog"
      }]
    }, {
      group: "Solutions",
      links: [{
        name: "Help center",
        href: "/help-center"
      }, {
        name: "Contact us",
        href: "/contact-us"
      }]
    }]
  }
];


export const Header = () => {
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const headerRef = useRef<HTMLElement>(null);
  const [isVerySmallScreen, setIsVerySmallScreen] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (headerRef.current && !headerRef.current.contains(event.target as Node)) {
        setActiveMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    
    // Check for very small screens (below 350px)
    const checkScreenWidth = () => {
      setIsVerySmallScreen(window.innerWidth < 350);
    };
    
    checkScreenWidth();
    window.addEventListener('resize', checkScreenWidth);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.addEventListener('resize', checkScreenWidth);
    };
  }, []);

  const isGroupArray = (items: NavigationLink[] | NavigationGroup[]): items is NavigationGroup[] => {
    return items.length > 0 && 'group' in items[0];
  };

  const MobileNavItem = ({ item, onClose }: { item: NavigationItem; onClose: () => void }) => {
    const [isOpen, setIsOpen] = useState(false);
    
    return (
      <div className="py-2">
        <button 
          className="flex w-full items-center justify-between text-white py-2" 
          onClick={() => setIsOpen(!isOpen)}
        >
          <span>{item.name}</span>
          <span className="text-xs">{isOpen ? "−" : "+"}</span>
        </button>
        
        {isOpen && item.items && (
          <div className="ml-4 mt-2 space-y-2 border-l border-white/10 pl-4">
            {!isGroupArray(item.items) ? (
              <div className="flex flex-col gap-4">
                {item.items.map(subItem => (
                  <Link 
                    key={subItem.name} 
                    href={subItem.href} 
                    className="text-white hover:text-primary transition-colors py-1" 
                    onClick={onClose}
                  >
                    {subItem.name}
                  </Link>
                ))}
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {item.items.map(group => (
                  <div key={group.group} className="mb-4">
                    <span className="text-sm text-gray-400 mb-2 block">{group.group}</span>
                    <div className="flex flex-col space-y-2">
                      {group.links.map(link => (
                        <Link 
                          key={link.name} 
                          to={link.href} 
                          className={`hover:text-primary transition-colors py-1 ${link.className || "text-white"}`} 
                          onClick={onClose}
                        >
                          {link.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const MobileNav = ({ onClose }: { onClose: () => void }) => {
    return (
      <div className="py-4 space-y-4">
        <div className="flex items-center justify-between mb-8">
          <Link href="/" onClick={onClose}>
            <img src="/lovable-uploads/3a6ba9b9-0afc-42d1-95d6-f27c11c60a8a.png" alt="Logo" className="h-12 w-12" />
          </Link>
        </div>
        
        <div className="space-y-2 divide-y divide-white/10">
          {navigation.map(item => (
            <MobileNavItem key={item.name} item={item} onClose={onClose} />
          ))}
        </div>
        
        <div className="pt-6 mt-6 border-t border-white/10">
          <div className="flex flex-col space-y-4">
            <Button
              className="bg-[#0066FF] hover:bg-[#0066FF]/90 text-white w-full"
              onClick={() => {
                window.open('https://my.fxnity.com/en/auth/sign-up', '_blank');
                onClose();
              }}
            >
              Register
            </Button>
            <Button
              variant="secondary"
              className="bg-gray-600 hover:bg-gray-700 text-white w-full"
              onClick={() => {
                window.open('https://my.fxnity.com/en/auth/sign-in', '_blank');
                onClose();
              }}
            >
              Sign in
            </Button>
            <div className="flex items-center justify-center gap-4 pt-4">
              <Link href="/help-center" className="text-gray-400 hover:text-white transition-colors flex items-center gap-2" onClick={onClose}>
                <HelpCircle className="h-5 w-5" />
                <span className="text-sm">Help</span>
              </Link>
              <button className="text-gray-400 hover:text-white transition-colors flex items-center gap-2">
                <Globe className="h-5 w-5" />
                <span className="text-sm">EN</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <header ref={headerRef} className="absolute top-0 left-0 right-0 z-50 backdrop-blur-lg border-b border-white/10 bg-black">
      <div className="container mx-auto px-4">
        <div className="flex h-20 items-center justify-between">
          <div className="flex items-center gap-12">
            <Link href="/" className="h-8">
              {isVerySmallScreen ? (
                <img src="/lovable-uploads/3a6ba9b9-0afc-42d1-95d6-f27c11c60a8a.png" alt="Logo" className="h-full w-auto" />
              ) : (
                <img src="/lovable-uploads/4aa46b68-610d-4d6f-83c0-61552798d9d0.png" alt="Logo" className="h-full w-auto" />
              )}
            </Link>
            <nav className="hidden lg:flex gap-8">
              {navigation.map(item => (
                <div key={item.name} className="relative group">
                  <button 
                    className={`flex items-center gap-1 text-sm ${activeMenu === item.name ? "text-white" : "text-gray-400 hover:text-white"} transition-colors`} 
                    onClick={() => setActiveMenu(activeMenu === item.name ? null : item.name)}
                  >
                    {item.name}
                    {item.icon}
                  </button>
                  
                  {item.items && activeMenu === item.name && (
                    <div className="absolute left-0 top-full pt-4">
                      <div className={`bg-black/90 backdrop-blur-lg rounded-lg p-6 ${item.name === "Trading" ? "min-w-[400px]" : item.name === "Company" ? "min-w-[300px]" : "min-w-[280px]"} shadow-xl border border-white/10`}>
                        {!isGroupArray(item.items) ? (
                          <div className="flex flex-col gap-4">
                            {item.items.map(subItem => (
                              <Link 
                                key={subItem.name} 
                                href={subItem.href} 
                                className="text-white hover:text-primary transition-colors" 
                                onClick={() => setActiveMenu(null)}
                              >
                                {subItem.name}
                              </Link>
                            ))}
                          </div>
                        ) : (
                          <div className="flex gap-16">
                            {item.items.map(group => (
                              <div key={group.group} className="flex flex-col gap-4 min-w-[180px]">
                                <span className="text-sm text-gray-400">{group.group}</span>
                                {group.links.map(link => (
                                  <Link 
                                    key={link.name} 
                                    href={link.href} 
                                    className={`hover:text-primary transition-colors ${link.className || "text-white"}`} 
                                    onClick={() => setActiveMenu(null)}
                                  >
                                    {link.name}
                                  </Link>
                                ))}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-4">
            <div className="lg:hidden">
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="ghost" className="p-0 h-10 w-10 text-gray-400 hover:text-white" aria-label="Open Menu">
                    <Menu className="h-6 w-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="p-0 w-full sm:max-w-md bg-black border-white/10">
                  <MobileNav onClose={() => {
                    const closeButton = document.querySelector('[data-radix-collection-item]');
                    if (closeButton && closeButton instanceof HTMLElement) {
                      closeButton.click();
                    }
                  }} />
                </SheetContent>
              </Sheet>
            </div>
            
            <Link href="/help-center" className="text-gray-400 hover:text-white transition-colors flex items-center gap-2">
              <HelpCircle className="h-5 w-5" />
              <span className="text-sm hidden md:inline">Help</span>
            </Link>
            <div className="hidden md:flex items-center gap-4">
              <Button
                className="bg-[#0066FF] hover:bg-[#0066FF]/90 text-white"
                onClick={() => window.open('https://my.fxnity.com/en/auth/sign-up', '_blank')}
              >
                Register
              </Button>
              <Button
                variant="secondary"
                className="bg-gray-600 hover:bg-gray-700 text-white"
                onClick={() => window.open('https://my.fxnity.com/en/auth/sign-in', '_blank')}
              >
                Sign in
              </Button>
            </div>
            <button className="text-gray-400 hover:text-white transition-colors flex items-center gap-2">
              <Globe className="h-5 w-5" />
              <span className="text-sm hidden md:inline">EN</span>
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};
